/**
 * Shared utilities for sample collection functionality
 * This ensures consistent handling of sample data across different components
 */

/**
 * Prepares sample data for database update
 * Extracts values from the sample data object and formats them for both
 * the dedicated columns and the sample_data JSON field
 */
function prepareSampleDataForUpdate(sampleData: Record<string, any>) {
  return {
    // Keep the original sample_data for backward compatibility
    sample_data: sampleData,
    
    // Extract values for dedicated columns
    temperature: sampleData.temperature ? parseFloat(sampleData.temperature) : null,
    ph: sampleData.ph ? parseFloat(sampleData.ph) : null,
    creatinine: sampleData.creatinine ? parseFloat(sampleData.creatinine) : null,
    hair_length: sampleData.hair_length ? parseFloat(sampleData.hair_length) : null,
    remaining_hair_length: sampleData.remaining_hair_length ? parseFloat(sampleData.remaining_hair_length) : null,
    extraction_site: sampleData.extraction_site || null,
    extraction_site_details: sampleData.extraction_site === 'Andere' ? sampleData.extraction_site_andere : null,
    hair_color: sampleData.hair_color || null,
    cosmetic_treatment: sampleData.cosmetic_treatment || null,
    cosmetic_treatment_details: sampleData.cosmetic_treatment === 'Behandelt' ? sampleData.cosmetic_treatment_behandelt : null
  };
}

/**
 * Updates a sample with collection data
 * @param supabase Supabase client
 * @param sampleId Sample ID
 * @param collectionData Collection data (date, time, collector info)
 * @param sampleData Sample measurement data
 * @returns Promise with the result of the update operation
 */
export async function updateSampleWithCollectionData(
  supabase: any,
  sampleId: string,
  collectionData: {
    collection_date: string;
    collection_time: string;
    collector_name: string;
    collector_id: string;
    internal_order_number?: string;
  },
  sampleData: Record<string, any>
) {
  const { collection_date, collection_time, collector_name, collector_id, internal_order_number } = collectionData;
  
  // Prepare the update data by combining collection data with sample data using our helper function
  const updateData = {
    status: 'collected',
    collection_date,
    collection_time,
    collector_name,
    collector_id,
    ...(internal_order_number ? { internal_order_number } : {}), // Only include if provided
    ...prepareSampleDataForUpdate(sampleData)
  };
  
  // Perform the update
  return await supabase
    .from('customer_samples')
    .update(updateData)
    .eq('id', sampleId);
}

/**
 * Updates a sample with result data
 * @param supabase Supabase client
 * @param sampleId Sample ID
 * @param resultData Result data (date, time, result, etc.)
 * @param sampleData Additional sample data (e.g., creatinine for urine samples)
 * @returns Promise with the result of the update operation
 */
export async function updateSampleWithResultData(
  supabase: any,
  sampleId: string,
  resultData: {
    received_date: string;
    received_time: string;
    internal_order_number?: string | null;
    external_order_number?: string | null;
    result: string;
    received_by: string;
    received_by_uuid: string;
  },
  sampleData: Record<string, any> = {}
) {
  const { received_date, received_time, result, received_by, received_by_uuid, internal_order_number, external_order_number } = resultData;
  
  // First, get the existing sample data to preserve values
  const { data: existingSample, error: fetchError } = await supabase
    .from('customer_samples')
    .select('sample_data, temperature, ph, creatinine, hair_length, remaining_hair_length, extraction_site, extraction_site_details, hair_color, cosmetic_treatment, cosmetic_treatment_details, internal_order_number, external_order_number')
    .eq('id', sampleId)
    .single();
    
  if (fetchError) {
    console.error('Error fetching existing sample data:', fetchError);
    // Continue with the update even if we couldn't fetch existing data
  }
  
  // Merge existing sample data with new data
  const mergedSampleData = {
    ...(existingSample?.sample_data || {}),
    ...sampleData
  };
  
  // Prepare the update data
  const updateData = {
    status: 'received',
    received_date,
    received_time,
    result,
    ...(internal_order_number ? { internal_order_number } : {}), // Only update if provided
    ...(external_order_number ? { external_order_number } : {}), // Only update if provided
    received_by,
    received_by_uuid,
    // Keep existing values for fields not being updated
    temperature: sampleData.temperature !== undefined ? 
      (sampleData.temperature ? parseFloat(sampleData.temperature) : null) : 
      existingSample?.temperature,
    ph: sampleData.ph !== undefined ? 
      (sampleData.ph ? parseFloat(sampleData.ph) : null) : 
      existingSample?.ph,
    creatinine: sampleData.creatinine !== undefined ? 
      (sampleData.creatinine ? parseFloat(sampleData.creatinine) : null) : 
      existingSample?.creatinine,
    hair_length: sampleData.hair_length !== undefined ? 
      (sampleData.hair_length ? parseFloat(sampleData.hair_length) : null) : 
      existingSample?.hair_length,
    remaining_hair_length: sampleData.remaining_hair_length !== undefined ? 
      (sampleData.remaining_hair_length ? parseFloat(sampleData.remaining_hair_length) : null) : 
      existingSample?.remaining_hair_length,
    extraction_site: sampleData.extraction_site || existingSample?.extraction_site,
    extraction_site_details: sampleData.extraction_site === 'Andere' ? 
      sampleData.extraction_site_andere : 
      existingSample?.extraction_site_details,
    hair_color: sampleData.hair_color || existingSample?.hair_color,
    cosmetic_treatment: sampleData.cosmetic_treatment || existingSample?.cosmetic_treatment,
    cosmetic_treatment_details: sampleData.cosmetic_treatment === 'Behandelt' ? 
      sampleData.cosmetic_treatment_behandelt : 
      existingSample?.cosmetic_treatment_details,
    // Update the sample_data JSONB field with merged data
    sample_data: mergedSampleData
  };
  
  // Perform the update
  return await supabase
    .from('customer_samples')
    .update({
      ...updateData,
      // Preserve existing order numbers if not provided
      ...(internal_order_number === undefined ? {} : { internal_order_number }),
      ...(external_order_number === undefined ? {} : { external_order_number })
    })
    .eq('id', sampleId);
}

/**
 * Fetches sample type information for a sample
 * @param supabase Supabase client
 * @param sampleId Sample ID
 * @returns Promise with the sample type information
 */
export async function fetchSampleTypeInfo(supabase: any, sampleId: string) {
  try {
    // First, get the sample_type_id from customer_samples
    const { data: sampleData, error: sampleError } = await supabase
      .from('customer_samples')
      .select('sample_type_id')
      .eq('id', sampleId)
      .single();
      
    if (sampleError) throw sampleError;
    
    if (!sampleData?.sample_type_id) {
      throw new Error('Sample type ID not found');
    }
    
    // Then, get the name from global_sample_types using the ID
    const { data: typeData, error: typeError } = await supabase
      .from('global_sample_types')
      .select('name, requires_temperature, requires_ph, requires_creatinine, requires_hair_length, requires_remaining_hair_length, requires_extraction_site, requires_hair_color, requires_cosmetic_treatment')
      .eq('id', sampleData.sample_type_id)
      .single();
      
    if (typeError) throw typeError;
    
    return typeData;
  } catch (err) {
    console.error('Error fetching sample type info:', err);
    throw err;
  }
}